/**
 *
 * AttributeLevel
 *
 */

import React, { useState, useCallback, Fragment } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { injectIntl, FormattedMessage } from 'react-intl';
import { Divider, Input, InputNumber, Button } from 'antd';
import { isUndefined } from 'lodash';

import { useDebounce, useDidUpdate } from 'utils/hooks';

import { WithPermissions, EDIT_PERMISSIONS } from 'containers/Permissions';
import { MOVE_DIRECTION } from 'containers/QuestionnairePage/constants';

import Pficon from 'components/Pficon';

import {
  getConjointNumericValue,
  getNumericFromText,
} from 'containers/QuestionnairePage/QuestionGroup/utils';

import HoverActionsField, {
  HoverActionsOverlayAnchor,
} from 'containers/QuestionnairePage/GenericQuestion/HoverActionsField';

import AttributeActions from './AttributeActions';
import { CONJOINT_ATTRIBUTE_TYPES } from './constants';
import messages from './messages';

function AttributeLevel({
  id,
  refId,
  attributeType,
  text: attributeLevelText,
  numericValue: attributeLevelValue,
  moveUpDisabled,
  moveDownDisabled,
  removeDisabled,
  isEmpty,
  onAddLevel,
  onRemoveLevel,
  onUpdateLevel,
  onMoveLevel,
  media,
  surveyId,
  intl,
  dataTest,
  onRemoveMedia,
  focus,
  hideActions,
}) {
  const [text, setText] = useState(attributeLevelText);
  const onChangeText = useCallback((e) => setText(e.target.value), []);
  const [numericValue, setNumericValue] = useState(attributeLevelValue);
  const [numericValueFromText, setNumericValueFromText] = useState(undefined);
  const onChangeValue = useCallback((newValue) => {
    setShouldUpdateNumericValue(true);
    setNumericValue(getNumericFromText(newValue));
  }, []);
  const [shouldUpdateNumericValue, setShouldUpdateNumericValue] =
    useState(false);

  const [debouncedText] = useDebounce(text, 500, { leading: true });
  useDidUpdate(() => {
    const newNumericValueFromText = getConjointNumericValue(
      debouncedText,
      attributeType,
    );
    setNumericValueFromText(newNumericValueFromText);

    if (isUndefined(newNumericValueFromText)) {
      setShouldUpdateNumericValue(false);
      setNumericValue(undefined);
    }

    onUpdateLevel(refId, {
      text: debouncedText,
      numericValue: newNumericValueFromText,
    });
  }, [debouncedText]);

  const [debouncedValue] = useDebounce(numericValue, 500);
  useDidUpdate(() => {
    if (shouldUpdateNumericValue) {
      setNumericValueFromText(undefined);
      onUpdateLevel(refId, { numericValue: debouncedValue });
    }
  }, [debouncedValue]);

  useDidUpdate(() => {
    const newNumericValueFromText = getConjointNumericValue(
      text,
      attributeType,
    );
    setNumericValueFromText(newNumericValueFromText);
  }, [attributeType]);

  const addImage = useCallback(
    ({ url, thumbnail, type, filename } = {}) => {
      if (!url) {
        onUpdateLevel(refId, { media: undefined });
        return;
      }

      onUpdateLevel(refId, { media: { url, thumbnail, type, filename } });
    },
    [onUpdateLevel, refId],
  );

  const removeImage = useCallback(() => {
    onRemoveMedia({
      mediaType: media.type,
      refId,
    });
    onUpdateLevel(refId, { media: undefined });
  }, [media, onRemoveMedia, onUpdateLevel]);

  const onMoveUp = () => {
    onMoveLevel(refId, MOVE_DIRECTION.UP);
  };

  const onMoveDown = () => {
    onMoveLevel(refId, MOVE_DIRECTION.DOWN);
  };

  const onRemove = () => {
    onRemoveLevel(refId);

    if (media && media.type) {
      onRemoveMedia({
        mediaType: media.type,
        refId,
      });
    }
  };

  return (
    <WithPermissions
      permission={[
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_VALUE_TEXT_EDIT,
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_VALUE_REORDER,
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_VALUE_REMOVE,
        EDIT_PERMISSIONS.CONJOINT_ATTRIBUTE_VALUE_EDIT_MEDIA,
      ]}
      itemToCheck={{ id }}
      key={refId}
    >
      {(
        _,
        [
          hasEditTextPermission,
          hasAttributeValueReOrderPermission,
          hasAttributeValueRemovePermission,
          hasAttributeValueEditMediaPermission,
        ],
      ) => (
        <li className="list-style-none margin-top-xxs" key={refId}>
          <div
            className={classNames(
              'flex',
              'flex-align-items--center',
              'question-answer',
            )}
          >
            <HoverActionsField
              className="flex flex-1"
              overlayClassName={classNames(
                'flex',
                'flex-align-items--center',
                'flex-justify-center',
              )}
              showOverlay={!isEmpty && !hideActions}
            >
              <HoverActionsOverlayAnchor
                className="flex flex-1"
                sideOverlay={
                  !isEmpty && !hideActions ? (
                    <Button
                      tabIndex="-1"
                      className={classNames(
                        'border-none',
                        'padding-none',
                        'shadow-none',
                        'text-size-h4',
                        'background-transparent',
                        'question-answer-delete-btn',
                        {
                          'color-blue-6-hover':
                            !removeDisabled &&
                            hasAttributeValueRemovePermission,
                          'background-transparent-hover pointer-events-none color-grey-7':
                            removeDisabled ||
                            !hasAttributeValueRemovePermission,
                        },
                      )}
                      disabled={
                        removeDisabled || !hasAttributeValueRemovePermission
                      }
                      onClick={onRemove}
                      data-testid={`remove-btn-${dataTest}`}
                    >
                      <Pficon type="close" />
                    </Button>
                  ) : null
                }
              >
                <Input
                  className={classNames(
                    'flex-1',
                    'border-none',
                    'pf-input-no-shadow',
                    'color-grey-10',
                    'padding-vertical-xs',
                    'background-transparent',
                    'text-size-secondary',
                    'ant-input-shadow-none',
                    'color-grey-8-placeholder',
                    'resize-none',
                    'background-grey-4',
                    'question-answer-input',
                  )}
                  style={{ height: 30 }}
                  placeholder={intl.formatMessage(messages.levelPlaceholder)}
                  value={text}
                  onChange={onChangeText}
                  autoFocus={focus && !isEmpty}
                  disabled={!hasEditTextPermission}
                  readOnly={!hasEditTextPermission}
                  onPressEnter={() => onAddLevel(refId)}
                />
              </HoverActionsOverlayAnchor>
            </HoverActionsField>

            {attributeType === CONJOINT_ATTRIBUTE_TYPES.PRICE && !isEmpty && (
              <Fragment>
                <span
                  className={classNames(
                    'margin-horizontal-xs',
                    'text-size-secondary',
                    {
                      'color-grey-9': hasEditTextPermission,
                      'background-transparent': !hasEditTextPermission,
                      'background-transparent-hover': !hasEditTextPermission,
                      'color-grey-7': !hasEditTextPermission,
                    },
                  )}
                >
                  <FormattedMessage {...messages.levelValueLabel} />
                </span>
                <InputNumber
                  id={`attr-value-${refId}`}
                  className={classNames(
                    'flex-1',
                    'border-none',
                    'pf-input-no-shadow',
                    'color-grey-10',
                    'background-transparent',
                    'text-size-secondary',
                    'ant-input-shadow-none',
                    'color-grey-8-placeholder',
                    'resize-none',
                    'background-grey-4',
                  )}
                  style={{ height: 30 }}
                  value={
                    isUndefined(numericValueFromText)
                      ? numericValue
                      : numericValueFromText
                  }
                  disabled={!hasEditTextPermission}
                  readOnly={!hasEditTextPermission}
                  parser={(value) => {
                    if (value) {
                      return value.replace(',', '.');
                    }
                    return value;
                  }}
                  onChange={onChangeValue}
                />
              </Fragment>
            )}

            {!isEmpty && !hideActions && (
              <AttributeActions
                refId={refId}
                surveyId={surveyId}
                media={media}
                addMedia={addImage}
                removeMedia={removeImage}
                uploadUrl={`/api/v3/media/draft-survey/${surveyId}?refId=${refId}`}
                permissions={[
                  hasAttributeValueReOrderPermission,
                  hasAttributeValueRemovePermission,
                  hasAttributeValueEditMediaPermission,
                ]}
                dataTest={dataTest}
                canHaveMoreThanOneAttribute={false}
                moveUpDisabled={moveUpDisabled}
                moveDownDisabled={moveDownDisabled}
                onMoveUp={onMoveUp}
                onMoveDown={onMoveDown}
                onRemove={onRemove}
                removeDisabled={removeDisabled}
              />
            )}
          </div>

          <Divider className="margin-none background-transparent" />
        </li>
      )}
    </WithPermissions>
  );
}

AttributeLevel.propTypes = {
  refId: PropTypes.string,
  attributeType: PropTypes.string,
  text: PropTypes.string,
  moveUpDisabled: PropTypes.bool,
  moveDownDisabled: PropTypes.bool,
  removeDisabled: PropTypes.bool,
  isEmpty: PropTypes.bool,
  onAddLevel: PropTypes.func,
  onUpdateLevel: PropTypes.func,
  onMoveLevel: PropTypes.func,
  onRemoveLevel: PropTypes.func,
  intl: PropTypes.object,
  dataTest: PropTypes.string,
  surveyId: PropTypes.number,
  media: PropTypes.object,
  focus: PropTypes.bool,
};

export default injectIntl(AttributeLevel);
